import React from "react";

import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import { styled } from "@mui/material/styles";
import { ListItemIcon, MenuItem, SvgIcon, useTheme } from "@mui/material";
import { useFieldArray, useFormContext } from "react-hook-form";
import AddIcon from "@mui/icons-material/Add";

import { Tag, TagI } from "constructs/Tag";
import { useDebouncedCallback } from "use-debounce";
import { skipToken } from "@reduxjs/toolkit/query";
import { useGetTagsByNameQuery } from "services/tag/tagApi";
import TagIcon from "Tag";
import { inputLabelStyles } from "./styles";

import { tags as tagMappings } from "assets/tags";
import LabelIcon from "@mui/icons-material/Label";

const strengths = [0, 1, 2, 3, 4, 5];

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

export interface ImagesEditorProps {
  name: string;
}

const TagsEditor = ({ name }: ImagesEditorProps) => {
  const theme = useTheme();
  const inputLabelProps = { sx: { color: theme.palette.text.primary } };
  const fieldBgColor = { backgroundColor: theme.palette.background.paper };

  const { control, getValues, setValue, register, watch } = useFormContext();

  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<readonly TagI[]>([]);
  const loading = open && options.length === 0;

  const { fields, append, prepend, remove, swap, move, insert } = useFieldArray(
    { name, control }
  );

  const [tagMagnitude, setTagMagnitude] = React.useState<number>(5);
  const [tagSpoilerLevel, setTagSpoilerLevel] = React.useState<number>(0);
  const [selectedTag, setSelectedTag] = React.useState<TagI | undefined>(
    undefined
  );

  const [inputValue, setInputValue] = React.useState<string>("");
  const [searchValue, setSearchValue] = React.useState<string>(inputValue);
  const [isTyping, setTyping] = React.useState(false);
  const { data, error, isFetching, isLoading } = useGetTagsByNameQuery(
    { name: searchValue, useDesc: false, searchApplicable: true } ?? skipToken,
    { skip: searchValue === "" }
  );

  const tagsWatch = watch(name);
  React.useEffect(() => {}, [tagsWatch]);

  const debounced = useDebouncedCallback(
    (value) => {
      (async () => {
        if (value === "") {
          setTyping(false);
          return;
        }

        // if (loading) {
        //   // If loading, don't fire yet
        //   return;
        // }
        setSearchValue(value);
        setTyping(false);
      })();
    },

    500
  );

  const handleMagnitudeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    let value = parseFloat(event.target.value);
    if (value < 0) value = 0;
    else if (value > 5) value = 5;

    value = parseFloat(value.toFixed(1));

    setTagMagnitude(value);
  };

  const handleSpoilerLevelChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    let value = parseFloat(event.target.value);
    if (value < 0) value = 0;
    else if (value > 5) value = 5;

    value = parseFloat(value.toFixed(1));

    setTagSpoilerLevel(value);
  };

  const handleInputValueChange = (
    event: React.SyntheticEvent,
    value: string
  ) => {
    setTyping(true);
    setInputValue(value);
  };

  const handleValueChange = (
    event: React.SyntheticEvent,
    value: TagI | null
  ) => {
    if (!value || options.findIndex((tag) => tag.id === value.id) === -1) {
      return;
    }
    setSelectedTag(value);
  };

  React.useEffect(updateSearchValue, [inputValue, debounced]);
  React.useEffect(updateOptions, [data, isLoading]);

  const handleAddTag = () => {
    if (!selectedTag) {
      return;
    }

    const path = `${name}`;
    const tags: Array<Tag> = getValues(path);

    if (tags.findIndex((tag) => tag.id === selectedTag.id) !== -1) {
      return;
    }

    const tag: Tag = {
      id: selectedTag.id,
      name: selectedTag.name,
      magnitude: tagMagnitude,
      spoiler: tagSpoilerLevel,
      description: selectedTag.description,
    };

    append(tag);
    setSelectedTag(undefined);
    setTagSpoilerLevel(0);
    setInputValue("");
    setSearchValue("");
  };

  const handleTagDelete = (index: number) => (event: any) => {
    remove(index);
  };

  function updateSearchValue() {
    debounced(inputValue);
  }

  function updateOptions() {
    if (isLoading) {
      return;
    }

    if (!data) {
      return;
    }

    setOptions(data);
  }

  return (
    <Stack>
      <Typography variant="h5" textAlign="left">
        Tags
      </Typography>
      <Box>
        <Stack direction="row" spacing={2}>
          <Autocomplete
            fullWidth
            inputValue={inputValue}
            id="async-tag-search"
            sx={{ width: 512 }}
            // open={open}
            // onOpen={() => {
            //   setOpen(true);
            // }}
            // onClose={() => {
            //   setOpen(false);
            // }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(option) => option.name} // This should AKCTUALLY NOT BE USED WITH render options, but it is broken without it so leave it until you can actually troubleshoot
            onInputChange={handleInputValueChange}
            options={options}
            loading={loading}
            renderInput={(params) => (
              <TextField
                {...params}
                sx={{
                  ".MuiInputBase-root": {
                    backgroundColor: theme.palette.background.paper,
                  },
                }}
                label="Tag Name"
                InputLabelProps={inputLabelProps}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {isTyping || isFetching ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
                // {...register(`${name}.tags`)}
              />
            )}
            // {...register("project")}
            onChange={handleValueChange}
            renderOption={(props, option, state) => (
              <MenuItem
                {...props}
                key={option.id}
                // sx={{ ':hover': { backgroundColor: 'red'}}}
                // sx={{ ".Mui-selected": { color: theme.palette.primary.main}}}
              >
                <ListItemIcon>
                  <SvgIcon
                    color="primary"
                    component={
                      tagMappings[option.id]?.icon ?? LabelIcon ?? null
                    }
                  />
                </ListItemIcon>
                {option.name}
              </MenuItem>
            )}
          />

          <TextField
            name="magnitude"
            id="magnitude"
            type="number"
            label="Magnitude"
            value={tagMagnitude}
            onChange={handleMagnitudeChange}
            InputLabelProps={inputLabelStyles(theme)}
            sx={{
              ".MuiInputBase-root": {
                backgroundColor: theme.palette.background.paper,
              },
            }}
          />

          <TextField
            name="spoiler"
            id="spoiler"
            label="Spoiler Level"
            type="number"
            value={tagSpoilerLevel}
            onChange={handleSpoilerLevelChange}
            InputLabelProps={inputLabelStyles(theme)}
            sx={{
              ".MuiInputBase-root": {
                backgroundColor: theme.palette.background.paper,
              },
            }}
          />

          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            onClick={handleAddTag}
          >
            Add Tag
          </Button>
        </Stack>
      </Box>
      <Box>
        <Stack direction="row">
          {fields.map((field, index) => {
            const tag = getValues(`${name}.${index}`) as Tag;
            return (
              <TagIcon
                key={field.id}
                tag={tag}
                onDelete={handleTagDelete(index)}
              />
            );
          })}
        </Stack>
      </Box>
    </Stack>
  );
};

export default TagsEditor;
